import type { MaybeRefOrGetter } from 'vue';
import type { MonospaceWorkspace, ResolvedMonospaceServiceAccount } from '#layers/data-model/public';
import { computed, ref, toValue } from 'vue';
import { useStore } from '#imports';
import { addServiceAccountToWorkspace, removeServiceAccountFromWorkspace } from '#layers/access/utils/api/service-account-workspaces';

export function useServiceAccountWorkspaces(
	serviceAccount: MaybeRefOrGetter<ResolvedMonospaceServiceAccount | null>,
	initialWorkspaces: MaybeRefOrGetter<MonospaceWorkspace[]>,
) {
	const startingWorkspaces = toValue(initialWorkspaces);
	const originalWorkspaces = toValue(serviceAccount) ? startingWorkspaces : [];

	const store = useStore();
	const { data: allWorkspaces } = store.MonospaceWorkspace.query((q) => q.many());

	const assignedWorkspaces = ref<MonospaceWorkspace[]>([...startingWorkspaces]);

	const availableWorkspaces = computed<MonospaceWorkspace[]>(() => {
		const modifiedIds = new Set(assignedWorkspaces.value.map((p) => p.id));
		return allWorkspaces.value?.filter((p) => !modifiedIds.has(p.id)) ?? [];
	});

	function addWorkspace(workspace: MonospaceWorkspace) {
		if (!assignedWorkspaces.value.some((p) => p.id === workspace.id)) {
			assignedWorkspaces.value = [...assignedWorkspaces.value, workspace];
		}
	}

	function removeWorkspace(workspace: MonospaceWorkspace) {
		assignedWorkspaces.value = assignedWorkspaces.value.filter((p) => p.id !== workspace.id);
	}

	async function saveChanges(serviceAccountId: string) {
		const originalIds = new Set(originalWorkspaces.map((p) => p.id));
		const modifiedIds = new Set(assignedWorkspaces.value.map((p) => p.id));

		const addedWorkspaces = assignedWorkspaces.value.filter((p) => !originalIds.has(p.id));
		const removedWorkspaces = originalWorkspaces.filter((p) => !modifiedIds.has(p.id));

		await Promise.all([
			...addedWorkspaces.map((workspace) => addServiceAccountToWorkspace(workspace.apiName, serviceAccountId)),
			...addedWorkspaces.map((workspace) => store.MonospaceWorkspaceMember.writeItem({ userId: serviceAccountId, workspaceId: workspace.id })),
			...removedWorkspaces.map((workspace) => removeServiceAccountFromWorkspace(workspace.apiName, serviceAccountId)),
			...removedWorkspaces.map((workspace) => store.MonospaceWorkspaceMember.clearItem(`${serviceAccountId}__${workspace.id}`)),
		]);
	}

	return {
		availableWorkspaces,
		assignedWorkspaces,
		addWorkspace,
		removeWorkspace,
		saveChanges,
	};
}
